// DisputeButton — file a structural challenge against a submission,
// citation, or block leaf.
//
// Opens an inline modal with a small textarea + stance picker. On
// submit, POSTs to /api/pgrl/disputes/file. Requires sign-in — if the
// user is anonymous, the button prompts them to sign in first.
//
// Composes with the disputes ledger primitive (v19.30). Every filing
// writes THREE cryptographic traces: disputes row, target.challenges,
// never_harm_events row.
import React, { useState } from "react";
import { AlertOctagon, Loader2, Send, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DisputeButton({
  targetType,
  targetId,
  targetLabel,
  disputeCount = 0,
  onFiled,
  compact = false,
  testidPrefix = "dispute",
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [stance, setStance] = useState("refute");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const signedIn = Boolean(user);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!signedIn) {
      toast.error("Sign in required to file a dispute.");
      return;
    }
    if (reason.trim().length < 8) {
      toast.error("Reason must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/pgrl/disputes/file`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          target_type: targetType,
          target_id: targetId,
          stance,
          reason: reason.trim(),
          evidence_url: evidenceUrl.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail?.detail || data?.detail || "Filing failed.");
      toast.success(
        `Dispute ${data.dispute.dispute_id} filed. Chain-anchored.`,
        { description: `Never-Harm event ${data.never_harm_event_hash?.slice(0, 12)}…` },
      );
      setOpen(false);
      setReason("");
      setEvidenceUrl("");
      setStance("refute");
      onFiled?.(data);
    } catch (err) {
      toast.error(String(err?.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testidPrefix}-button-${targetId}`}
        className={`inline-flex items-center gap-1.5 border transition-colors ${
          compact
            ? "px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.22em]"
            : "px-3 py-1.5 text-xs font-mono uppercase tracking-[0.28em]"
        } ${
          disputeCount > 0
            ? "border-amber-500/60 text-amber-300 hover:border-amber-400 hover:bg-amber-500/[0.06]"
            : "border-zinc-700 text-zinc-400 hover:border-amber-500/60 hover:text-amber-300"
        }`}
        title={
          signedIn
            ? "File a structural dispute against this claim"
            : "Sign in to dispute"
        }
      >
        <AlertOctagon className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
        {compact ? "dispute" : "File dispute"}
        {disputeCount > 0 ? (
          <span className={compact ? "text-[9px]" : "text-[10px]"}>
            · {disputeCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !submitting && setOpen(false)}
          data-testid={`${testidPrefix}-modal-${targetId}`}
        >
          <div
            className="w-full max-w-lg border border-amber-500/40 bg-[#0b0b0d]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-amber-500/[0.03]">
              <div className="flex items-center gap-2 text-amber-300 font-mono text-[10px] uppercase tracking-[0.3em]">
                <AlertOctagon className="h-3.5 w-3.5" />
                File dispute · {targetType}
              </div>
              <button
                onClick={() => !submitting && setOpen(false)}
                className="text-zinc-500 hover:text-zinc-200"
                data-testid={`${testidPrefix}-close`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {!signedIn ? (
              <div className="p-5 text-sm text-zinc-300">
                <p>Sign in required to file a structural dispute.</p>
                <a
                  href="/signin"
                  className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-500/60 text-emerald-300 font-mono text-[10px] uppercase tracking-[0.28em] hover:bg-emerald-500 hover:text-zinc-950"
                >
                  Sign in <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-4">
                {targetLabel ? (
                  <div className="text-[11px] text-zinc-400 leading-relaxed border-l-2 border-amber-500/40 pl-3 line-clamp-3">
                    {targetLabel}
                  </div>
                ) : null}

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5 font-mono">
                    Stance
                  </label>
                  <div className="flex gap-2">
                    {["refute", "qualify"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStance(s)}
                        className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.28em] ${
                          stance === s
                            ? "border-amber-500 text-amber-200 bg-amber-500/[0.08]"
                            : "border-zinc-800 text-zinc-500 hover:border-zinc-600"
                        }`}
                        data-testid={`${testidPrefix}-stance-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-[10px] text-zinc-600 leading-relaxed">
                    {stance === "refute"
                      ? "Refute: the claim is structurally wrong."
                      : "Qualify: partially correct — needs additional context."}
                  </p>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5 font-mono">
                    Reason · min 8 chars
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    placeholder="What is structurally wrong with this claim? Cite specifics."
                    className="w-full bg-black/60 border border-zinc-800 focus:border-amber-500/60 outline-none text-sm text-zinc-100 p-2.5 font-sans placeholder:text-zinc-700"
                    data-testid={`${testidPrefix}-reason`}
                  />
                  <div className="mt-1 flex justify-between text-[10px] text-zinc-600 font-mono">
                    <span>chained · HMAC-sealed · non-repudiable</span>
                    <span>{reason.length}/1000</span>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1.5 font-mono">
                    Evidence URL · optional
                  </label>
                  <input
                    type="url"
                    value={evidenceUrl}
                    onChange={(e) => setEvidenceUrl(e.target.value)}
                    placeholder="https://…"
                    className="w-full bg-black/60 border border-zinc-800 focus:border-amber-500/60 outline-none text-sm text-zinc-100 p-2 font-mono placeholder:text-zinc-700"
                    data-testid={`${testidPrefix}-evidence`}
                  />
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={submitting || reason.trim().length < 8}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/60 text-amber-200 font-mono text-[11px] uppercase tracking-[0.3em] hover:bg-amber-500 hover:text-zinc-950 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`${testidPrefix}-submit`}
                  >
                    {submitting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    {submitting ? "Filing…" : "File dispute"}
                  </button>
                  <button
                    type="button"
                    onClick={() => !submitting && setOpen(false)}
                    disabled={submitting}
                    className="px-3 py-2 text-zinc-500 hover:text-zinc-200 font-mono text-[10px] uppercase tracking-[0.28em]"
                  >
                    cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
